import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';

import Button from '~/Components/Global/Form/Button';
import Card from '~/Components/Layout/Card/Card';
import AddGoogleAccount from '~/Components/Pages/AddAccount/AddGoogleAccount';
import AddOutlookAccount from '~/Components/Pages/AddAccount/AddOutlookAccount';
import AddAppleAccount from '~/Components/Pages/AddAccount/AddAppleAccount';
import { getCalendarAccounts } from '~/Redux/Modules/CalendarAccounts';
import { MONTH } from '~/Config/DateRangeTypes';
import { APPLE, GOOGLE, OUTLOOK } from '~/Config/Drivers';
import { setMonthType } from '~/Redux/Modules/DateRange';
import mParticle, { ScreenViewEvent, StartPlanningEvent } from '~/Providers/mParticle';
import history from '~/Providers/History';
import { initializeOnboardingAfterFetch } from '~/Redux/Modules/Onboarding/helpers';

import './AddAccount.scss';
import { useMountEffect } from '~/util';

const AddAccount = ({ match }) => {
  const dispatch = useDispatch();
  const isFetching = useSelector(({ CalendarAccounts }) => CalendarAccounts.isFetching);
  const currentMonth = useSelector(({ DateRange }) => DateRange.start.clone().startOf(MONTH));
  const calendarAccounts = useSelector(({ CalendarAccounts }) => CalendarAccounts.data);

  const [trackingCode, setTrackingCode] = useState(null);
  const [showTrackingPixel, setShowTrackingPixel] = useState(false);

  useMountEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const sasTracking = search.get('sas_tracking');
    if (sasTracking && (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'beta')) {
      setTrackingCode(sasTracking);
      setShowTrackingPixel(true);
      history.replace('/add-accounts');
    }

    dispatch(getCalendarAccounts());
    mParticle.logEvent(new ScreenViewEvent('Manage Calendars'));
  });

  const monthView = monthStart => dispatch(setMonthType(monthStart.clone()));

  const handleStartPlanning = () => {
    mParticle.logEvent(new StartPlanningEvent());
    dispatch(initializeOnboardingAfterFetch());
    monthView(currentMonth);
  };

  const googleAccounts = calendarAccounts.filter(({ driver }) => driver === GOOGLE);
  const appleAccounts = calendarAccounts.filter(({ driver }) => driver === APPLE);
  const outlookAccounts = calendarAccounts.filter(({ driver }) => driver === OUTLOOK);

  return (
    <Card customLogo={<div>Manage Calendars</div>}>
      {showTrackingPixel && (
        <>
          <img
            src={`https://www.shareasale.com/sale.cfm?tracking=${trackingCode}&amount=0.00&merchantID=138423&transtype=lead`}
            width="1"
            height="1"
          />
          <script src="https://www.dwin1.com/47023.js" type="text/javascript" defer="defer" />
        </>
      )}
      <div className="add-account">
        <small className="center">
          Artful Agenda beautifully integrates with Google Calendar, iCalendar, and Outlook
          Calendar. Connect to one or more to continue.
        </small>
        {googleAccounts.map(calendarAccount => (
          <React.Fragment key={calendarAccount.id}>
            <hr />
            <AddGoogleAccount isFetching={isFetching} account={calendarAccount} />
          </React.Fragment>
        ))}
        <hr />
        <AddGoogleAccount isFetching={isFetching} />

        {appleAccounts.map(calendarAccount => (
          <React.Fragment key={calendarAccount.id}>
            <hr />
            <AddAppleAccount isFetching={isFetching} account={calendarAccount} />
          </React.Fragment>
        ))}
        <hr />
        <AddAppleAccount isFetching={isFetching} />

        {outlookAccounts.map(calendarAccount => (
          <React.Fragment key={calendarAccount.id}>
            <hr />
            <AddOutlookAccount isFetching={isFetching} account={calendarAccount} match={match} />
          </React.Fragment>
        ))}
        <hr />
        <AddOutlookAccount isFetching={isFetching} match={match} />

        {calendarAccounts.filter(({ driver }) => [GOOGLE, APPLE, OUTLOOK].includes(driver)) && (
          <div className="center margin-top margin-bottom">
            <Button
              onClick={handleStartPlanning}
              className="margin-top btn btn-default pill"
              text="START PLANNING!"
            />
          </div>
        )}
      </div>
    </Card>
  );
};

AddAccount.propTypes = {
  currentMonth: propTypes.object,
  googleAccount: propTypes.object,
  appleAccount: propTypes.object,
  isFetching: propTypes.bool,
  outlookAccount: propTypes.object,
  match: propTypes.object,
};

export default AddAccount;
